import {Button, Input, Space} from "antd";
import {
  StyledVariantListItemChildrenWrapper,
  StyledVariantListItemTopInputAndButtonWrapper,
  StyledVariantListItemTopWrapper,
  StyledVariantListItemWrapper,
} from "./styles";
import {VariantListItemField} from "./types";
import {FormField, FormFieldError} from "../../../../../design-system/form-field";
import {Controller, UseFieldArrayRemove, useFormContext} from "react-hook-form";
import {ProductVariantsFormFieldsProps, ProductVariantsFormValues} from "../types";
import {DeleteOutlined, DownOutlined} from "@ant-design/icons";
import {VariantOptionList} from "../variant-option-list";
import {motion, AnimatePresence} from "framer-motion";
import {useEffect} from "react";
import {ProductPricingFormFieldsType} from "../../pricing/types";

type ProductVariantsListItemFormFieldsProps = {
  index: number;
  item: VariantListItemField;
  remove: UseFieldArrayRemove;
  openIndexes: number[];
  toggleIndex: (index: number) => void;
  productCurrencies: ProductPricingFormFieldsType["currencies"];
  productSelectedPriceType: "paid" | "free";
  hydratedRef: ProductVariantsFormFieldsProps["hydratedRef"];
};
export function ProductVariantsListItemFormFields({
  index,
  item,
  remove,
  openIndexes,
  toggleIndex,
  productCurrencies,
  productSelectedPriceType,
  hydratedRef,
}: ProductVariantsListItemFormFieldsProps) {
  const {
    control,
    formState: {errors},
  } = useFormContext<ProductVariantsFormValues>();
  const hasError = errors.variants ? !!errors.variants[index] : false;

  useEffect(() => {
    if (hasError && !openIndexes.includes(index)) {
      toggleIndex(index);
    }
  }, [hasError, index, openIndexes, toggleIndex]);

  return (
    <StyledVariantListItemWrapper>
      {/* ACCORDION TOP */}
      <StyledVariantListItemTopWrapper>
        <p>Add-on name</p>
        <StyledVariantListItemTopInputAndButtonWrapper>
          <div className="input-wrapper">
            <FormField
              id={`variantListItem-${item.id}`}
              renderInput={(id) => (
                <Controller
                  control={control}
                  name={`variants.${index}.name`}
                  render={({
                    field: {onChange, value, ref, onBlur},
                    fieldState: {error},
                  }) => (
                    <Space size={8} direction="vertical" style={{display: "flex"}}>
                      <Input
                        id={id}
                        status={error?.message ? "error" : ""}
                        onChange={onChange}
                        onBlur={onBlur}
                        size="large"
                        value={value}
                        placeholder=" e.g Additional social media templates"
                        ref={ref}
                      />
                      <FormFieldError errorMessage={error?.message} />
                    </Space>
                  )}
                />
              )}
            />
          </div>
          <div className="button-wrapper">
            {index !== 0 && (
              <Button
                // Group removal is form-only; the actual DELETE is deferred to
                // "save" (useSaveAddOns diffs the loaded snapshot and calls the
                // v2 delete), matching how option removal works.
                onClick={() => remove(index)}
                type="text"
                shape="circle"
                icon={<DeleteOutlined />}
                size="small"
              />
            )}
            {!hasError && (
              <Button
                type="text"
                shape="circle"
                icon={<DownOutlined rotate={openIndexes.includes(index) ? 0 : 180} />}
                size="small"
                onClick={() => toggleIndex(index)}
              />
            )}
          </div>
        </StyledVariantListItemTopInputAndButtonWrapper>
      </StyledVariantListItemTopWrapper>

      {/* ACCORDION CHILDREN */}
      <AnimatePresence initial={false}>
        {/* {openIndexes.includes(index) && ( */}
        <motion.div
          key="content"
          initial={false}
          animate={openIndexes.includes(index) ? "open" : "collapsed"}
          // exit="collapsed"
          variants={{
            open: {opacity: 1, height: "auto"},
            collapsed: {opacity: 0, height: 0},
          }}
          transition={{duration: 0.8, ease: [0.04, 0.62, 0.23, 0.98]}}
        >
          <StyledVariantListItemChildrenWrapper>
            <div className="text-description">
              Options let you offer multiple versions of an add-on, each with its own
              price, so your customers can choose what suits their needs and budget.{" "}
            </div>
            <VariantOptionList
              variantIndex={index}
              productCurrencies={productCurrencies}
              productSelectedPriceType={productSelectedPriceType}
              hydratedRef={hydratedRef}
            />
          </StyledVariantListItemChildrenWrapper>
        </motion.div>
        {/* )} */}
      </AnimatePresence>
    </StyledVariantListItemWrapper>
  );
}
